import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { BellIcon } from '../../components/BellIcon';
import { hostCardStyle } from '../../components/HostSettingsCard';
import { useBackTarget } from '../../hooks/useBackTarget';
import { formatEuropeanDate } from '../../utils/dateFormat';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const font = "'Inria Serif', serif";

const cardStyle = {
  ...hostCardStyle,
  width: '100%',
  maxWidth: 360,
  padding: '16px 14px',
};

const formatEuro = (value) => `${Number(value || 0).toFixed(2)}€`;

export default function HostPayoutHistoryPage() {
  const navigate = useNavigate();
  const goBack = useBackTarget();
  const [history, setHistory] = useState({ payouts: [], ledger_entries: [], monthly_summaries: [], balances: {} });
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState('');

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/host/payouts/history`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setHistory(response.data);
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, []);

  const filteredSummaries = useMemo(() => {
    if (!selectedMonth) return history.monthly_summaries || [];
    return (history.monthly_summaries || []).filter((item) => item.payout_month === selectedMonth);
  }, [history.monthly_summaries, selectedMonth]);

  const filteredLedger = useMemo(() => {
    if (!selectedMonth) return history.ledger_entries || [];
    return (history.ledger_entries || []).filter((item) => item.month_key === selectedMonth);
  }, [history.ledger_entries, selectedMonth]);

  return (
    <div className="app-shell" data-testid="host-payout-history-page" style={{ backgroundColor: 'var(--app-bg)', minHeight: '100vh', paddingBottom: 32 }}>
      <div style={{ padding: '16px 20px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <button data-testid="host-payout-history-back-button" onClick={goBack} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
          <ChevronLeft size={34} color="var(--text)" strokeWidth={1.8} />
        </button>
        <button data-testid="host-payout-history-bell-button" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
          <BellIcon />
        </button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '0 16px' }}>
        <img src="/allure-logo.png" alt="Allure" style={{ width: 144, height: 126, objectFit: 'contain', marginTop: 8 }} />
        <h1 style={{ fontFamily: font, fontSize: 30, fontWeight: 700, color: 'var(--text)', margin: '14px 0 16px' }}>Payout Ledger</h1>

        <div data-testid="host-payout-history-summary-card" style={cardStyle}>
          <div style={{ display: 'grid', gap: 8 }}>
            <p style={{ fontFamily: font, fontSize: 22, fontWeight: 700, color: 'var(--text)', margin: 0 }}>Host totals</p>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
              <span style={{ fontFamily: font, fontSize: 16, color: 'var(--text-2)' }}>Paid out</span>
              <strong data-testid="host-payout-history-total-paid" style={{ fontFamily: font, fontSize: 18 }}>{formatEuro(history.total_paid_out)}</strong>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
              <span style={{ fontFamily: font, fontSize: 16, color: 'var(--text-2)' }}>Pending</span>
              <strong data-testid="host-payout-history-total-pending" style={{ fontFamily: font, fontSize: 18 }}>{formatEuro(history.total_pending)}</strong>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
              <span style={{ fontFamily: font, fontSize: 16, color: 'var(--text-2)' }}>Paid host tips</span>
              <strong data-testid="host-payout-history-total-tips" style={{ fontFamily: font, fontSize: 18 }}>{formatEuro(history.total_tips)}</strong>
            </div>
            <label htmlFor="host-payout-history-month-input" style={{ display: 'grid', gap: 6, marginTop: 6 }}>
              <span style={{ fontFamily: font, fontSize: 16, color: 'var(--text-2)' }}>Filter by month</span>
              <input
                id="host-payout-history-month-input"
                type="month"
                value={selectedMonth}
                onChange={(event) => setSelectedMonth(event.target.value)}
                data-testid="host-payout-history-month-input"
                style={{ borderRadius: 14, border: '1px solid rgba(112, 63, 246, 0.18)', padding: '10px 12px', fontSize: 15 }}
              />
            </label>
          </div>
        </div>

        <div style={{ width: '100%', maxWidth: 360, marginTop: 16, display: 'grid', gap: 14 }}>
          <div style={cardStyle}>
            <p style={{ fontFamily: font, fontSize: 20, fontWeight: 700, color: 'var(--text)', margin: 0 }}>Monthly batches</p>
            {filteredSummaries.length === 0 ? (
              <p data-testid="host-payout-history-empty" style={{ fontFamily: font, fontSize: 16, color: 'var(--text-2)', margin: '8px 0 0' }}>No monthly payout batches yet.</p>
            ) : filteredSummaries.map((item) => (
              <div key={item.id} data-testid={`host-payout-history-summary-${item.id}`} style={{ borderTop: '1px solid var(--hairline)', paddingTop: 10, marginTop: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
                  <div>
                    <p style={{ fontFamily: font, fontSize: 18, color: 'var(--text)', margin: 0 }}>{item.payout_month} · Batch {item.batch_sequence}</p>
                    <p style={{ fontFamily: font, fontSize: 14, color: 'var(--text-2)', margin: '4px 0 0' }}>Generated {formatEuropeanDate(item.generated_at)} · Status {item.status}</p>
                  </div>
                  <strong style={{ fontFamily: font, fontSize: 20 }}>{formatEuro(item.total_amount)}</strong>
                </div>
                <button
                  type="button"
                  data-testid={`host-payout-history-view-statement-${item.id}`}
                  onClick={() => navigate(`/host/settings/payouts/statements/${item.id.split(':')[0]}`)}
                  style={{ marginTop: 10, border: '1px solid rgba(112, 63, 246, 0.16)', borderRadius: 12, padding: '10px 12px', background: 'var(--surface)', color: 'var(--text)', cursor: 'pointer', fontFamily: font, fontSize: 16, fontWeight: 700 }}
                >
                  View statement
                </button>
              </div>
            ))}
          </div>

          <div style={cardStyle}>
            <p style={{ fontFamily: font, fontSize: 20, fontWeight: 700, color: 'var(--text)', margin: 0 }}>Ledger entries</p>
            {loading ? <p style={{ fontFamily: font, fontSize: 16, color: 'var(--text-2)', margin: '8px 0 0' }}>Loading…</p> : null}
            {!loading && filteredLedger.length === 0 ? <p style={{ fontFamily: font, fontSize: 16, color: 'var(--text-2)', margin: '8px 0 0' }}>No ledger entries for this month.</p> : null}
            {filteredLedger.map((item) => (
              <div key={item.id} data-testid={`host-payout-history-card-${item.id}`} style={{ borderTop: '1px solid var(--hairline)', paddingTop: 10, marginTop: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
                  <div>
                    <p style={{ fontFamily: font, fontSize: 18, color: 'var(--text)', margin: 0 }}>{item.source_label}</p>
                    <p style={{ fontFamily: font, fontSize: 14, color: '#666', margin: '4px 0 0' }}>{item.guest_name || item.listing_name || 'Allure host payout'} · {formatEuropeanDate(item.occurred_at)}</p>
                  </div>
                  <strong style={{ fontFamily: font, fontSize: 18, color: item.status === 'paid' ? '#04A628' : 'var(--text)' }}>{formatEuro(item.total_amount)}</strong>
                </div>
                <p style={{ fontFamily: font, fontSize: 13, color: 'var(--text-2)', margin: '6px 0 0' }}>
                  Commission {formatEuro(item.commission_amount)} · Tips {formatEuro(item.host_tip_amount)} · Rewards {formatEuro(item.reward_amount)} · {item.status}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}